import React, { useState } from "react";
import { m, AnimatePresence } from "framer-motion";
import { MousePointerClick, Sparkles, Share2 } from "lucide-react";

export const InteractiveDemo = () => {
  const [activeStep, setActiveStep] = useState(0);

  const steps = [
    {
      id: 0,
      label: "Select Design",
      icon: MousePointerClick,
      description: "Choose your Figma frames with a simple selection",
      features: ["Point & Click Selection", "Multi-frame Support"]
    },
    {
      id: 1,
      label: "Export & Share",
      icon: Share2,
      description: "Export documentation to your preferred platform",
      features: ["PDF Export", "Markdown Format", "Web Export"]
    },
  ];

  return (
    <section className="section-padding bg-gradient-to-b from-background to-background/50" style={{ contain: "layout style" }}>
      <div className="container-custom">
        {/* Header */}
        <m.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-50px" }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
          style={{ willChange: "transform, opacity" }}
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 backdrop-blur-sm border border-primary/20 mb-6">
            <Sparkles className="w-4 h-4 text-primary" />
            <span className="text-sm font-medium">Interactive Demo</span>
          </div>
          <h2 className="text-4xl md:text-6xl font-bold mb-6">
            See It <span className="gradient-text">In Action</span>
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto">
            Watch how youXforma transforms designs into perfect documentation
          </p>
        </m.div>

        <div className="max-w-6xl mx-auto">
          {/* Step Navigation */}
          <div className="grid md:grid-cols-2 gap-6 mb-12">
            {steps.map((step, index) => {
              const Icon = step.icon;
              const isActive = activeStep === step.id;
              
              return (
                <m.button
                  key={step.id}
                  onClick={() => setActiveStep(step.id)}
                  className={`text-left p-8 rounded-2xl transition-all duration-300 ${
                    isActive 
                      ? 'glass-card ring-2 ring-primary/50' 
                      : 'glass-card hover:ring-2 hover:ring-primary/20'
                  }`}
                  whileHover={{ y: -2 }}
                  whileTap={{ scale: 0.99 }}
                  style={{ willChange: "transform" }}
                >
                  <div className="flex items-start gap-4 mb-4">
                    <div className={`w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0 transition-all duration-300 ${
                      isActive 
                        ? 'bg-primary text-white' 
                        : 'bg-primary/10 text-primary'
                    }`}>
                      <Icon className="w-7 h-7" />
                    </div>
                    <div className="flex-1">
                      <span className="text-xs font-medium text-muted-foreground mb-1 block">
                        Step {index + 1}
                      </span>
                      <h3 className="text-xl font-semibold mb-2">
                        {step.label}
                      </h3>
                    </div>
                  </div>

                  <p className="text-muted-foreground mb-4">
                    {step.description}
                  </p>

                  <div className="flex flex-wrap gap-2">
                    {step.features.map((feature, i) => (
                      <span
                        key={i}
                        className={`px-3 py-1 rounded-full text-sm transition-all ${
                          isActive
                            ? 'bg-primary/10 text-primary border border-primary/20'
                            : 'bg-muted text-muted-foreground'
                        }`}
                      >
                        {feature}
                      </span>
                    ))}
                  </div>
                </m.button>
              );
            })}
          </div>

          {/* Content Display */}
          <m.div
            className="glass-card rounded-2xl p-12 text-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <AnimatePresence mode="wait">
              <m.div
                key={activeStep}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
              >
                <div className={`inline-flex w-24 h-24 rounded-2xl items-center justify-center mb-6 ${
                  activeStep === 0 ? 'bg-purple-500/10' : 'bg-emerald-500/10'
                }`}>
                  {React.createElement(steps[activeStep].icon, {
                    className: `w-12 h-12 ${activeStep === 0 ? 'text-purple-500' : 'text-emerald-500'}`,
                    strokeWidth: 1.5
                  })}
                </div>
                <p className="text-lg text-muted-foreground">
                  {steps[activeStep].description}
                </p>
              </m.div>
            </AnimatePresence>
          </m.div>
        </div>
      </div>
    </section>
  );
};